<template>
  <div class="plan-month">
    <div class="plan-month__head">
      <div
        v-for="label in weekdayLabels"
        :key="label"
        class="plan-month__weekday"
      >
        {{ label }}
      </div>
    </div>
    <div class="plan-month__grid">
      <div
        v-for="day in days"
        :key="day.iso"
        class="plan-month__cell"
        :class="{
          'plan-month__cell--outside': !day.inMonth,
          'plan-month__cell--today': day.isToday,
        }"
        @click="onCellClick(day)"
      >
        <div class="plan-month__day-number">{{ day.dayNumber }}</div>
        <button
          v-for="slot in slotsByDate[day.iso] || []"
          :key="slot.id"
          type="button"
          class="plan-month__event"
          :class="`plan-month__event--${eventVariant(slot)}`"
          @click.stop="emit('slot-click', slot)"
        >
          <span class="plan-month__event-time">{{ slot.scheduledLocal.time }}</span>
          <span
            v-if="monthEventLabel(slot, t)"
            class="plan-month__event-title"
          >
            {{ monthEventLabel(slot, t) }}
          </span>
          <v-skeleton-loader
            v-else
            class="plan-month__event-skeleton"
            type="text"
            :boilerplate="!isCardLoading(slot) && !isImageLoading(slot)"
          />
          <v-icon
            class="plan-month__event-platform"
            :class="`plan-card__platform--${(slot.platform || '').toLowerCase()}`"
            :icon="platformIcon(slot)"
            size="14"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { formatIso } from './calendarDates'
import {
  eventVariant,
  groupSlotsByDate,
  isCardLoading,
  isImageLoading,
  monthEventLabel,
  platformIcon,
} from './slotCard'

const props = defineProps({
  weekdayLabels: {
    type: Array,
    default: () => [],
  },
  days: {
    type: Array,
    default: () => [],
  },
  slots: {
    type: Array,
    default: () => [],
  },
  creatable: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['slot-click', 'create-slot'])

const { t } = useI18n()
const slotsByDate = computed(() => groupSlotsByDate(props.slots))

function onCellClick(day) {
  if (!props.creatable || day.iso <= formatIso(new Date())) return
  emit('create-slot', { date: day.iso, time: '09:00' })
}
</script>

<style scoped>
.plan-month {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  border-top: 1px solid #ececec;
  border-bottom: 1px solid #ececec;
  background: #fff;
}

.plan-month__head,
.plan-month__grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.plan-month__weekday {
  padding: 8px 4px;
  border-bottom: 1px solid #ececec;
  color: #222;
  font-size: 13px;
  font-weight: 700;
  text-align: center;
}

.plan-month__grid {
  flex: 1;
  min-height: 0;
  overflow: auto;
  grid-auto-rows: minmax(112px, 1fr);
  padding-bottom: 200px;
}

.plan-month__cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 4px;
  border-top: 1px solid #ececec;
  border-left: 1px solid #ececec;
}

.plan-month__cell:nth-child(7n + 1) {
  border-left: none;
}

.plan-month__cell--outside {
  background: #fafafa;
}

.plan-month__cell--outside .plan-month__day-number {
  color: #b0b0b0;
}

.plan-month__cell--today {
  background: rgba(var(--v-theme-primary), 0.05);
}

.plan-month__day-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #333;
  font-size: 12px;
  font-weight: 700;
}

.plan-month__cell--today .plan-month__day-number {
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  color: #fff;
}

.plan-month__event {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  padding: 2px 6px;
  border: 1px solid rgb(var(--v-theme-primary));
  border-radius: 6px;
  background: #fff;
  color: rgb(var(--v-theme-primary));
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  text-align: left;
  cursor: pointer;
}

.plan-month__event--failed {
  border-color: #c62828;
  color: #c62828;
}

.plan-month__event--draft,
.plan-month__event--generating {
  border-style: dashed;
}

.plan-month__event-time {
  flex: 0 0 auto;
}

.plan-month__event-title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plan-month__event-skeleton {
  flex: 1;
  min-width: 0;
  height: 12px;
}

.plan-month__event-skeleton :deep(.v-skeleton-loader__text) {
  margin: 0;
}

.plan-month__event-platform {
  flex: 0 0 auto;
  margin-left: auto;
}

.plan-card__platform--instagram {
  background: radial-gradient(circle at 30% 110%, #fccc63, #f77737 35%, #e1306c 60%, #833ab4);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

@media (max-width: 1100px) {
  .plan-month__head,
  .plan-month__grid {
    min-width: 780px;
  }
}
</style>
